/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import i18n from '@/shell/i18n/i18n';
// @ts-expect-error
import {CommercialLicenseView} from '@proto/teamdev/licensing/q/commercial/commercial_license_view_pb';

function enumText(relativeKey: string | number): string {
  return i18n.t('lserv-app.shell.model.teamdev.licensing.q.commercial.' + relativeKey) as string;
}

const propsByLicenseTypes = new Map<CommercialLicenseView.Type, TypeProps>([
  [CommercialLicenseView.Type.SINGLE_DEV, {
    icon: 'license_developer'
  }],
  [CommercialLicenseView.Type.PROJECT, {
    icon: 'license_project'
  }],
  [CommercialLicenseView.Type.COMPANY_WIDE, {
    icon: 'license_company_wide'
  }],
  [CommercialLicenseView.Type.PERSONAL, {
    icon: 'license_personal'
  }],
  [CommercialLicenseView.Type.SINGLE_DEV_SOURCE_CODE, {
    icon: 'license_developer_source_code'
  }],
  [CommercialLicenseView.Type.PROJECT_SOURCE_CODE, {
    icon: 'license_project_source_code'
  }],
  [CommercialLicenseView.Type.COMPANY_WIDE_SOURCE_CODE, {
    icon: 'license_company_source_code'
  }]
]);

/**
 * The data associated with each individual commercial license type.
 */
interface TypeProps {
  icon: string;
}

/**
 * Utilities for the `CommercialLicenseView.Type` type, which enrich the type with properties
 * needed for displaying it in the UI.
 */
export class CommercialLicenseTypes {

  /**
   * Returns the full name of this commercial license type (usable when the main and suffix part of
   * the name don't need to be distinguished, e.g. for displaying with different styles).
   *
   * See `nameMain` an `nameSuffix` for the constituent parts that the full name is composed of.
   */
  static fullName(type: CommercialLicenseView.Type): string {
    return (this.nameMain(type) + ' ' + this.nameSuffix(type)).trim();
  }

  /**
   * A main part of the license type's name. Note that it's not self-sufficient without the "suffix"
   * part (see the `nameSuffix` property).
   */
  static nameMain(type: CommercialLicenseView.Type): string {
    return enumText('CommercialLicenseViewType.' + type);
  }

  /**
   * A suffix part of the license type's name. This complements the main part provided via
   * the `nameMain` property.
   */
  static nameSuffix(type: CommercialLicenseView.Type): string {
    return enumText('CommercialLicenseViewType_suffix.' + type);
  }

  /**
   * Icon name for the license type.
   */
  static icon(type: CommercialLicenseView.Type): string {
    const props = propsByLicenseTypes.get(type);
    if (props === undefined) {
      throw new Error('Unknown licenseType: ' + type);
    }
    return props.icon;
  }
}
